
import Vue from 'vue'
import {
  loadStripe,
  Stripe,
  StripeCardCvcElement,
  StripeCardExpiryElement,
  StripeCardNumberElement,
  StripeElements,
} from '@stripe/stripe-js'
import { mapActions, mapGetters } from 'vuex'
import { stripeTestApiKey, stripeLiveApiKey, getAppRootURL } from '../../config'
import { RSelect, RInput, RButton, RCard } from '@/components/'
import BillingHistory from '@/components/account/BillingHistory.vue'
import CardDetails from '@/components/account/CardDetails.vue'
import { CardSummary } from '@/api/types/user'
import store from '@/store/root'
import { trialPeriodExpiryDate } from '@/services/bundles'

export const STRIPE_API_VERSION = '2020-08-27'

type Data = {
  editingCard: boolean
  // Billing:
  fullName: string
  addressLine1: string
  addressLine2: string
  city: string
  postalCode: string
  country: string

  // Default payment method:
  stripe: Stripe | null
  cardholderName: string
  cardNumberElement: StripeCardNumberElement | null
  cardExpiryElement: StripeCardExpiryElement | null
  cardCvcElement: StripeCardCvcElement | null

  cardNumberComplete: boolean
  cardExpiryComplete: boolean
  cardCvcComplete: boolean

  cardNumberError: string
  cardExpiryError: string
  cardCvcError: string
}

export default Vue.extend({
  name: 'EditBillingInfo',
  head: {
    link: [{ rel: 'canonical', href: `${getAppRootURL()}/account/billing`, id: 'canonical' }],
  },
  components: {
    RCard,
    RInput,
    RButton,
    BillingHistory,
    CardDetails,
    RSelect,
  },
  props: {
    missing_payment_only: {
      type: Boolean,
      default: false,
    },
    showBilling: {
      type: Boolean,
      default: true,
    },
  },
  data(): Data {
    const profile = this.$store.state.user.profile
    const billing = profile.billing || {}
    return {
      editingCard: false,
      // Billing:
      fullName: billing.full_name || '',
      addressLine1: billing.address_line_1 || '',
      addressLine2: billing.address_line_2 || '',
      city: billing.city || '',
      postalCode: billing.postal_code || '',
      country: billing.country || '',

      // Default payment method:
      stripe: null,
      cardholderName: '',
      cardNumberElement: null,
      cardExpiryElement: null,
      cardCvcElement: null,

      cardNumberComplete: false,
      cardExpiryComplete: false,
      cardCvcComplete: false,

      cardNumberError: '',
      cardExpiryError: '',
      cardCvcError: '',
    }
  },
  async mounted() {
    if (this.user.profile.environment === 'dev') {
      this.stripe = await loadStripe(stripeTestApiKey, { apiVersion: STRIPE_API_VERSION })
    } else {
      this.stripe = await loadStripe(stripeLiveApiKey, { apiVersion: STRIPE_API_VERSION })
    }

    if (!this.stripe) {
      await store.dispatch('notification/error', 'Unable to load Stripe')
      return
    }
    const elements: StripeElements = this.stripe.elements()

    const elementStyles = {
      base: {
        color: '#E2E2E3',
        fontSize: '14px',
        fontFamily: 'Open Sans, sans-serif',
        lineHeight: '44px',
        '::placeholder': {
          color: '#9C9C9C',
        },
        ':focus': {
          color: '#fff',
        },
      },
      invalid: {
        color: '#FC3333',
        iconColor: '#fa755a',
      },
    }

    const elementClasses = {
      focus: 'focused',
      empty: 'empty',
      invalid: 'invalid',
    }

    const options = {
      style: elementStyles,
      classes: elementClasses,
    }
    this.cardNumberElement = elements.create('cardNumber', options)
    this.cardNumberElement.mount('#card-number-element')

    this.cardExpiryElement = elements.create('cardExpiry', options)
    this.cardExpiryElement.mount('#card-expiry-element')

    this.cardCvcElement = elements.create('cardCvc', options)
    this.cardCvcElement.mount('#card-cvc-element')

    this.cardNumberElement.on('change', ({ error, complete }: any) => {
      if (error) {
        this.cardNumberError = error.message
      } else {
        this.cardNumberError = ''
      }
      this.cardNumberComplete = complete
    })

    this.cardExpiryElement.on('change', ({ error, complete }: any) => {
      if (error) {
        this.cardExpiryError = error.message
      } else {
        this.cardExpiryError = ''
      }
      this.cardExpiryComplete = complete
    })

    this.cardCvcElement.on('change', ({ error, complete }: any) => {
      if (error) {
        this.cardCvcError = error.message
      } else {
        this.cardCvcError = ''
      }
      this.cardCvcComplete = complete
    })
  },
  methods: {
    ...mapActions({
      updateBilling: 'user/updateBilling',
      updatePaymentMethod: 'user/updatePaymentMethod',
    }),
    async saveCard() {
      const { cardholderName, cardNumberElement } = this
      await this.updatePaymentMethod({
        stripe: this.stripe,
        cardholderName,
        card: cardNumberElement,
      })

      if (this.cardNumberElement) {
        this.cardNumberElement.clear()
      }
      if (this.cardExpiryElement) {
        this.cardExpiryElement.clear()
      }
      if (this.cardCvcElement) {
        this.cardCvcElement.clear()
      }
      this.cardholderName = ''
      this.editingCard = false
    },
    async changeCard() {
      this.editingCard = true
    },
    async saveBilling() {
      const { fullName, addressLine1, addressLine2, city, postalCode, country } = this
      this.updateBilling({
        billing: {
          fullName,
          addressLine1: addressLine1,
          addressLine2: addressLine2,
          city,
          postalCode: postalCode,
          country,
        },
      })
    },
  },
  computed: {
    ...mapGetters({
      billing: 'user/billing',
      billingUpdateRequest: 'user/billingUpdateRequest',
      paymentMethodUpdateRequest: 'user/paymentMethodUpdateRequest',
      user: 'user/user',
      theme: 'theme',
    }),
    hasPaymentMethod(): boolean {
      return this.billing.stripe_active_payment_method_identifier
    },
    showExistingCard(): boolean {
      return Boolean(this.hasPaymentMethod && !this.editingCard && this.cardSummary)
    },
    trialPeriodExpiryDate(): string {
      const trialEnd = this.user.profile.storage_plan?.trial_end
      if (trialEnd) {
        return trialPeriodExpiryDate(trialEnd)
      }
      return ''
    },
    saveCardDisabled(): boolean {
      return (
        this.paymentMethodUpdateRequest.pending ||
        !this.cardholderName ||
        !this.cardNumberComplete ||
        !this.cardExpiryComplete ||
        !this.cardCvcComplete
      )
    },
    saveBillingDisabled(): boolean {
      return (
        this.billingUpdateRequest.pending ||
        !(this.addressLine1 && this.city && this.postalCode && this.country)
      )
    },
    cardSummary(): CardSummary | null {
      const card = this.billing && this.billing.card ? this.billing.card : null
      if (!card) {
        return null
      }
      return card
    },
    labelBackground(): string {
      return 'var(--bgTertiary)'
    },
    invoicesHeader(): string {
      return this.missing_payment_only ? 'Outstanding invoices' : 'Billing history'
    },
  },
})
